// Template 4: Gold Metal Plate
// Luxury gold foil design with 3D engraved effects

import { useState, useEffect } from 'react'
import { 
  Phone, 
  MessageCircle, 
  Mail, 
  Share2, 
  Download,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Globe,
  MapPin,
  QrCode
} from 'lucide-react'

export function GoldMetalTemplate({ vcard, onSaveContact, onShare }) {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 })

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100
      const y = (e.clientY / window.innerHeight) * 100
      setMousePos({ x, y })
    }

    const handleScroll = () => {
      const y = (window.scrollY / window.innerHeight) * 50
      setMousePos(prev => ({ ...prev, y: 50 + y }))
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div className="gold-metal-template">
      <div className="gm-background">
        <div 
          className="gm-light-reflection"
          style={{ 
            background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, rgba(255,255,255,0.4) 0%, transparent 50%)`
          }}
        ></div>
      </div>

      <div className="gm-card">
        <div className="gm-card-inner">
          {/* Decorative Border */}
          <div className="gm-border-pattern"></div>

          {/* Profile */}
          <div className="gm-profile">
            <div 
              className="gm-avatar"
              style={{ 
                backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none'
              }}
            >
              {!vcard.profileImage && (
                <span>{vcard.name?.[0] || 'U'}</span>
              )}
            </div>
            <h1 className="gm-name">{vcard.name || 'Your Name'}</h1>
            <div className="gm-divider">
              <span></span>
              <span className="gm-ornament">◆</span>
              <span></span>
            </div>
            <p className="gm-title">{vcard.title || 'Your Title'}</p>
            {vcard.company && <p className="gm-company">{vcard.company}</p>}
          </div>

          {/* Laser-cut Icons */}
          <div className="gm-actions">
            {vcard.phone && (
              <a href={`tel:${vcard.phone}`} className="gm-icon-btn">
                <Phone size={22} />
                <span>Call</span>
              </a>
            )}
            {vcard.whatsapp && (
              <a 
                href={`https://wa.me/${vcard.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="gm-icon-btn"
              >
                <MessageCircle size={22} />
                <span>WhatsApp</span>
              </a>
            )}
            {vcard.email && (
              <a href={`mailto:${vcard.email}`} className="gm-icon-btn">
                <Mail size={22} />
                <span>Email</span>
              </a>
            )}
            <button onClick={onShare} className="gm-icon-btn">
              <QrCode size={22} />
              <span>QR Code</span>
            </button>
          </div>

          {/* About */}
          {vcard.about && (
            <div className="gm-section">
              <p className="gm-about">{vcard.about}</p>
            </div>
          )}

          {/* Services */}
          {vcard.services && vcard.services.length > 0 && (
            <div className="gm-section">
              <h3 className="gm-section-title">Expertise</h3>
              <div className="gm-services">
                {vcard.services.map((service, i) => (
                  <span key={i} className="gm-tag">{service}</span>
                ))}
              </div>
            </div>
          )}

          {/* Social */}
          {vcard.social && Object.values(vcard.social).some(v => v) && (
            <div className="gm-social-row">
              {vcard.social.linkedin && (
                <a 
                  href={vcard.social.linkedin.startsWith('http') ? vcard.social.linkedin : `https://linkedin.com/in/${vcard.social.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gm-social-icon"
                >
                  <Linkedin size={18} />
                </a>
              )}
              {vcard.social.instagram && (
                <a 
                  href={vcard.social.instagram.startsWith('http') ? vcard.social.instagram : `https://instagram.com/${vcard.social.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gm-social-icon"
                >
                  <Instagram size={18} />
                </a>
              )}
              {vcard.social.facebook && (
                <a 
                  href={vcard.social.facebook.startsWith('http') ? vcard.social.facebook : `https://facebook.com/${vcard.social.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gm-social-icon"
                >
                  <Facebook size={18} />
                </a>
              )}
              {vcard.social.twitter && (
                <a 
                  href={vcard.social.twitter.startsWith('http') ? vcard.social.twitter : `https://twitter.com/${vcard.social.twitter.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gm-social-icon"
                >
                  <Twitter size={18} />
                </a>
              )}
              {vcard.social.website && (
                <a 
                  href={vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gm-social-icon"
                >
                  <Globe size={18} />
                </a>
              )}
            </div>
          )}

          {/* Location */}
          {vcard.address && (
            <a 
              href={`https://maps.google.com/?q=${encodeURIComponent(vcard.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="gm-location"
            >
              <MapPin size={16} />
              <span>{vcard.address}</span>
            </a>
          )}

          {/* Save Button */}
          <button onClick={onSaveContact} className="gm-save-btn">
            <Download size={18} />
            Save to Contacts
          </button>

          {/* Footer Ornament */}
          <div className="gm-footer-ornament">
            <span></span>
            <span className="gm-diamond">◇</span>
            <span></span>
          </div>
        </div>
      </div>

      <footer className="gm-footer">
        <p>Crafted with <a href="/">Digital vCard</a></p>
      </footer>
    </div>
  )
}

export default GoldMetalTemplate
